import React, { createContext, useState, useEffect, useCallback } from 'react'
import { SwallerContainer } from './SwallerContainer'
import { SwallerModal } from './SwallerModal'
import { setSwallerMethods } from './swallerBridge'

export const SwallerContext = createContext(null)

const MAX_TOASTS = 4

export const SwallerProvider = ({ children }) => {
  const [toasts, setToasts] = useState([])
  const [modal, setModal] = useState(null)

  // Toast management
  const addToast = (type, text, options = {}) => {
    const id = Date.now() + Math.random()
    const duration = options.duration || 3000

    const toast = {
      id,
      type,
      text,
      duration
    }

    setToasts((prev) => {
      const newToasts = [toast, ...prev]
      // Keep only max 4 toasts
      return newToasts.slice(0, MAX_TOASTS)
    })

    return id
  }

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }, [])

  // Toast methods (non-blocking)
  const success = useCallback((text, options = {}) => {
    addToast('success', text, options)
  }, [])

  const error = useCallback((text, options = {}) => {
    addToast('error', text, options)
  }, [])

  const warning = useCallback((text, options = {}) => {
    addToast('warning', text, options)
  }, [])

  const info = useCallback((text, options = {}) => {
    addToast('info', text, options)
  }, [])

  const welcome = useCallback((text, options = {}) => {
    addToast('welcome', text, options)
  }, [])

  // Modal methods (blocking)
  const question = useCallback(async (text, options = {}) => {
    return new Promise((resolve) => {
      setModal({
        type: 'question',
        text,
        dangerMode: options.dangerMode || false,
        onClose: (result) => {
          setModal(null)
          resolve(result)
        }
      })
    })
  }, [])

  const input = useCallback(async (text, placeholder = '') => {
    return new Promise((resolve) => {
      setModal({
        type: 'input',
        text,
        placeholder,
        onClose: (value) => {
          setModal(null)
          resolve(value)
        }
      })
    })
  }, [])

  const value = {
    // Toast state
    toasts,
    removeToast,
    // Toast methods
    success,
    error,
    warning,
    info,
    welcome,
    // Modal state
    modal,
    // Modal methods
    question,
    input
  }

  // Expose methods globally for backward compatibility
  useEffect(() => {
    setSwallerMethods({ success, error, warning, info, welcome, question, input })
  }, [success, error, warning, info, welcome, question, input])

  return (
    <SwallerContext.Provider value={value}>
      {children}
      <SwallerContainer toasts={toasts} onDismiss={removeToast} />
      {modal && <SwallerModal {...modal} />}
    </SwallerContext.Provider>
  )
}
